
import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Guest as GuestType, guests, episodes } from "@/data/mockData";
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  MessageSquare, 
  Save, 
  Plus, 
  Edit, 
  Trash2,
  Mail,
  Phone,
  User,
  Globe,
  Bookmark,
  Lightbulb,
  FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import StatusBadge from "@/components/StatusBadge";
import { formatDistanceToNow, parseISO, format } from "date-fns";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const Guest = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [guest, setGuest] = useState<GuestType | null>(null);
  const [prompt, setPrompt] = useState<string>("");
  const [researchHighlights, setResearchHighlights] = useState<string[]>([]);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [newHighlight, setNewHighlight] = useState<string>("");
  const [newQuestion, setNewQuestion] = useState<string>("");
  const [isEditingPrompt, setIsEditingPrompt] = useState<boolean>(false);
  const [isGeneratingNotes, setIsGeneratingNotes] = useState<boolean>(false);
  const [showNotes, setShowNotes] = useState<string>("");
  
  const relatedEpisodes = episodes.filter(episode => episode.guestId === id);

  useEffect(() => {
    const foundGuest = guests.find(g => g.id === id);
    if (foundGuest) {
      setGuest(foundGuest);
      setPrompt("Please research this guest's background in technology and their views on artificial intelligence and machine learning. Focus on their recent publications and speaking engagements.");
      setResearchHighlights([
        "Published a paper on reinforcement learning in Nature last year",
        "Spoke at AI Conference 2023 about ethical considerations in AI development",
        "Holds 3 patents related to machine learning algorithms",
        "Founded an AI ethics committee at their company"
      ]);
      setSuggestedQuestions([
        "Can you elaborate on the findings from your recent paper in Nature?",
        "How do you see reinforcement learning evolving in the next 5 years?",
        "What ethical guidelines do you think are most important for AI development?",
        "Tell us about your experience founding the AI ethics committee."
      ]);
    } else {
      navigate("/not-found");
    }
  }, [id, navigate]);

  const handleSavePrompt = () => {
    toast.success("Research prompt saved");
    setIsEditingPrompt(false);
  };

  const handleAddHighlight = () => {
    if (newHighlight.trim()) {
      setResearchHighlights([...researchHighlights, newHighlight]);
      setNewHighlight("");
      toast.success("Research highlight added");
    }
  };

  const handleRemoveHighlight = (index: number) => {
    const updatedHighlights = [...researchHighlights];
    updatedHighlights.splice(index, 1);
    setResearchHighlights(updatedHighlights);
    toast.success("Research highlight removed");
  };

  const handleAddQuestion = () => {
    if (newQuestion.trim()) {
      setSuggestedQuestions([...suggestedQuestions, newQuestion]);
      setNewQuestion("");
      toast.success("Question added");
    }
  };

  const handleRemoveQuestion = (index: number) => {
    const updatedQuestions = [...suggestedQuestions];
    updatedQuestions.splice(index, 1);
    setSuggestedQuestions(updatedQuestions);
    toast.success("Question removed");
  };

  const generateShowNotes = () => {
    // In a real application, this would call an API to generate show notes
    setIsGeneratingNotes(true);
    
    // Simulate API call with timeout
    setTimeout(() => {
      const generatedNotes = `
# Episode with ${guest?.name}

## Introduction
${guest?.name} is a ${guest?.title} at ${guest?.company}, known for their work in ${guest?.topics.join(", ")}.

## Key Discussion Points
${researchHighlights.map(highlight => `- ${highlight}`).join("\n")}

## Summary
In this episode, we discussed ${guest?.name}'s background, their work at ${guest?.company}, and their expertise in ${guest?.topics[0]}. 
We explored their recent publications and insights on the future of technology in their field.

## Follow-up
- Connect with ${guest?.name} on ${guest?.socialLinks?.linkedin ? "LinkedIn" : "their website"}
- Check out their recent work on ${guest?.topics[0]}
      `;
      
      setShowNotes(generatedNotes);
      setIsGeneratingNotes(false);
      toast.success("Show notes generated successfully");
    }, 2000);
  };

  if (!guest) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Loading guest information...</p>
      </div>
    );
  }

  return (
    <main className="container mx-auto px-4 py-6 animate-fade-in">
      <div className="mb-8">
        <Button
          variant="ghost"
          className="mb-4 text-muted-foreground hover:text-foreground" 
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <h1 className="text-3xl font-bold">{guest.name}</h1>
          <div className="flex items-center gap-2">
            <Button 
              variant="default" 
              onClick={generateShowNotes}
              disabled={isGeneratingNotes}
              className="flex items-center"
            >
              <FileText className="h-4 w-4 mr-2" />
              {isGeneratingNotes ? "Generating..." : "Generate Show Notes"}
            </Button>
            <StatusBadge status={guest.status} className="text-sm px-3 py-1" />
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="md:col-span-1">
          <CardHeader className="pb-0">
            <CardTitle className="text-xl">Profile</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center mb-6">
              <Avatar className="h-32 w-32 mb-4">
                <AvatarImage src={guest.imageUrl} alt={guest.name} />
                <AvatarFallback className="text-4xl">
                  {guest.name.split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              <h3 className="text-xl font-medium text-center">{guest.name}</h3>
              <p className="text-muted-foreground text-center">{guest.title} at {guest.company}</p>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Email</p>
                  <p className="text-sm text-muted-foreground">{guest.email}</p>
                </div>
              </div>
              
              {guest.phone && (
                <div className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Phone</p>
                    <p className="text-sm text-muted-foreground">{guest.phone}</p>
                  </div>
                </div>
              )}
              
              {guest.socialLinks && (
                <div className="flex items-start gap-3">
                  <Globe className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Online</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {guest.socialLinks.website && (
                        <a href={guest.socialLinks.website} target="_blank" rel="noopener noreferrer" className="text-xs px-2 py-1 bg-secondary rounded-md hover:bg-secondary/80 transition-colors">
                          Website
                        </a>
                      )}
                      {guest.socialLinks.twitter && (
                        <a href={guest.socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="text-xs px-2 py-1 bg-secondary rounded-md hover:bg-secondary/80 transition-colors">
                          Twitter
                        </a>
                      )}
                      {guest.socialLinks.linkedin && (
                        <a href={guest.socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="text-xs px-2 py-1 bg-secondary rounded-md hover:bg-secondary/80 transition-colors">
                          LinkedIn
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {guest.scheduledDate && (
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Scheduled</p>
                    <p className="text-sm text-muted-foreground">
                      {format(parseISO(guest.scheduledDate), "MMMM d, yyyy")}
                      <span className="text-xs ml-1 text-muted-foreground/70">
                        ({formatDistanceToNow(parseISO(guest.scheduledDate), { addSuffix: true })})
                      </span>
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card className="md:col-span-2">
          <CardHeader className="pb-0">
            <CardTitle className="text-xl">Biography & Topics</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <p className="mb-6 text-muted-foreground">{guest.bio}</p>
            
            <h4 className="text-sm font-medium mb-2">Areas of Expertise</h4>
            <div className="flex flex-wrap gap-2 mb-6">
              {guest.topics.map((topic, index) => (
                <span key={index} className="text-xs px-3 py-1 bg-secondary rounded-full">
                  {topic}
                </span>
              ))}
            </div>
            
            {guest.tags && guest.tags.length > 0 && (
              <>
                <h4 className="text-sm font-medium mb-2">Tags</h4>
                <div className="flex flex-wrap gap-2">
                  {guest.tags.map((tag, index) => (
                    <span key={index} className="text-xs px-3 py-1 bg-secondary/50 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Show notes section - only displayed when notes are generated */}
      {showNotes && (
        <Card className="mb-8">
          <CardHeader className="pb-0">
            <CardTitle className="text-xl flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Generated Show Notes
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="whitespace-pre-line bg-secondary/20 p-4 rounded-md">
              {showNotes}
            </div>
            <div className="flex justify-end mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  // In a real app, this would save the notes to a database
                  toast.success("Show notes saved successfully");
                }}
                className="mr-2"
              >
                <Save className="h-4 w-4 mr-2" />
                Save Notes
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  navigator.clipboard.writeText(showNotes);
                  toast.success("Show notes copied to clipboard");
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-clipboard mr-2"><rect width="8" height="4" x="8" y="2" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/></svg>
                Copy to Clipboard
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      <div className="grid grid-cols-1 gap-6 mb-8">
        <Card>
          <CardHeader className="pb-0 flex flex-row items-center justify-between">
            <CardTitle className="text-xl flex items-center">
              <MessageSquare className="h-5 w-5 mr-2" />
              Research Prompt
            </CardTitle>
            {!isEditingPrompt ? (
              <Button size="sm" variant="ghost" onClick={() => setIsEditingPrompt(true)}>
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </Button>
            ) : (
              <Button size="sm" variant="default" onClick={handleSavePrompt}>
                <Save className="h-4 w-4 mr-1" />
                Save
              </Button>
            )}
          </CardHeader>
          <CardContent className="pt-6">
            {isEditingPrompt ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  This prompt will guide the research for this guest. Be specific about what information you're looking for.
                </p>
                <textarea 
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="w-full min-h-[120px] px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter research prompts here..."
                />
              </div>
            ) : (
              <p className="text-muted-foreground">{prompt}</p>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-0">
            <CardTitle className="text-xl flex items-center">
              <Bookmark className="h-5 w-5 mr-2" />
              Research Highlights
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Key findings from research about this guest that would be valuable during the interview.
              </p>
              
              <div className="space-y-2">
                {researchHighlights.map((highlight, index) => (
                  <div key={index} className="flex items-start gap-2 p-3 bg-secondary/20 rounded-md group">
                    <div className="flex-1">{highlight}</div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0"
                      onClick={() => handleRemoveHighlight(index)}
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
              
              <div className="flex gap-2 mt-4">
                <Input
                  value={newHighlight}
                  onChange={(e) => setNewHighlight(e.target.value)}
                  placeholder="Add a new research highlight..."
                  className="flex-1"
                />
                <Button variant="secondary" onClick={handleAddHighlight}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-0">
            <CardTitle className="text-xl flex items-center">
              <Lightbulb className="h-5 w-5 mr-2" />
              Suggested Questions
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Questions prepared for the interview based on research and guest expertise.
              </p>
              
              <div className="space-y-2">
                {suggestedQuestions.map((question, index) => (
                  <div key={index} className="flex items-start gap-2 p-3 bg-secondary/20 rounded-md group">
                    <div className="flex-1">{question}</div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0"
                      onClick={() => handleRemoveQuestion(index)}
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
              
              <div className="flex gap-2 mt-4">
                <Input
                  value={newQuestion}
                  onChange={(e) => setNewQuestion(e.target.value)}
                  placeholder="Add a new question..."
                  className="flex-1"
                />
                <Button variant="secondary" onClick={handleAddQuestion}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {relatedEpisodes.length > 0 && (
        <Card>
          <CardHeader className="pb-0">
            <CardTitle className="text-xl">Related Episodes</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              {relatedEpisodes.map((episode) => (
                <div key={episode.id} className="flex items-center gap-4 p-3 bg-secondary/10 rounded-md">
                  <div className="w-12 h-12 rounded-md overflow-hidden">
                    <img src={episode.thumbnailUrl} alt={episode.title} className="object-cover w-full h-full" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium">{episode.title}</h4>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        {format(parseISO(episode.recordingDate), "MMM d, yyyy")}
                      </span>
                      {episode.duration && (
                        <span className="flex items-center">
                          <Clock className="h-3 w-3 mr-1" />
                          {episode.duration}
                        </span>
                      )}
                      <StatusBadge status={episode.status} className="text-xs" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </main>
  );
};

export default Guest;
